import { useState, type CSSProperties } from 'react';
import { Check, ArrowRight, ArrowLeft, Sparkles, Building2, ShieldCheck } from 'lucide-react';
import { Button, Eyebrow } from '../components/ui';
import { useStore } from '../store';

// First-run setup wizard: walks a new deployment's first admin through the essentials, linking to
// the existing Settings tabs and the new-organisation modal. Each step is a guided hand-off rather
// than a duplicate form, so there is one source of truth for each setting.
const card: CSSProperties = { padding: 28, marginBottom: 14 };

const STEPS = [
  { key: 'brand', icon: Sparkles, title: 'Make it yours', body: 'Set your product name, logo and palette so the platform reflects your organisation.', cta: 'Open branding settings' },
  { key: 'org', icon: Building2, title: 'Create your first organisation', body: 'An organisation groups an assessment programme and its cohort analytics. Create one to get started.', cta: 'Create an organisation' },
  { key: 'govern', icon: ShieldCheck, title: 'Confirm governance', body: 'Review the default assessment mode, per-organisation k-anonymity and the identified-roster policy. Anonymised, role-level analytics are on by default.', cta: 'Open experience settings' },
] as const;

export function Setup() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState<Set<string>>(new Set());
  const setScreen = useStore((s) => s.setScreen);
  const openCampaignModal = useStore((s) => s.openCampaignModal);

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const markAndAct = () => {
    setDone((d) => new Set(d).add(s.key));
    if (s.key === 'org') openCampaignModal('create');
    else setScreen('settings');
  };

  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 760, margin: '0 auto', padding: '48px 32px 80px' }}>
        <div className="animate-fadeUp" style={{ marginBottom: 18 }}>
          <span className="text-green-text uppercase tracking-eyebrow" style={{ fontSize: 11, fontWeight: 700 }}>Welcome</span>
          <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', margin: '10px 0 0' }}>Set up your deployment</h1>
          <div className="text-storm" style={{ fontSize: 14, marginTop: 7 }}>A few minutes to brand the platform and create your first programme.</div>
        </div>

        {/* step indicator */}
        <div role="list" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {STEPS.map((st, i) => (
            <div role="listitem" key={st.key} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= step ? 'var(--brand-primary, #00D700)' : '#E2E2E6' }} />
          ))}
        </div>

        <div className="bg-white border border-panel rounded-card" style={card}>
          <Eyebrow>Step {step + 1} of {STEPS.length}</Eyebrow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 8px' }}>
            <s.icon size={22} className="text-green" />
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{s.title}</h2>
            {done.has(s.key) && <Check size={18} className="text-green" aria-label="done" />}
          </div>
          <p className="text-storm" style={{ fontSize: 14, lineHeight: 1.55, margin: '0 0 20px' }}>{s.body}</p>
          <Button onClick={markAndAct}>{s.cta}</Button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
          <button type="button" onClick={() => setStep((n) => Math.max(0, n - 1))} disabled={step === 0}
            className="text-storm" style={{ background: 'transparent', border: 'none', fontSize: 13, fontWeight: 600, cursor: step === 0 ? 'default' : 'pointer', opacity: step === 0 ? 0.4 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={15} /> Back
          </button>
          {isLast ? (
            <Button onClick={() => setScreen('welcome')}><Check size={15} /> Finish setup</Button>
          ) : (
            <Button variant="ghost" onClick={() => setStep((n) => Math.min(STEPS.length - 1, n + 1))}>
              Next <ArrowRight size={15} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
